import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function Analytics() {
  const { user } = useApp();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  async function loadAnalytics() {
    try {
      const { data } = await api.get(`/analytics/my?period=${period}`);
      setData(data);
    } catch (e) {
      setData(generateDemoData());
    } finally {
      setLoading(false);
    }
  }

  function generateDemoData() {
    const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'];
    return {
      income: [1200, 1800, 1500, 2200, 1900, 2500],
      expenses: [400, 600, 500, 700, 600, 800],
      labels: months,
      totals: { income: 11100, expenses: 3600, profit: 7500, invoices: 24, clients: 8 },
      topClients: [
        { name: 'ABC Corp', amount: 3500 },
        { name: 'XYZ Ltd', amount: 2800 },
        { name: 'Demo Inc', amount: 1900 },
      ],
      taxForecast: { vat: 850, pit: 420, zus: 380, total: 1650 },
    };
  }

  function BarChart({ data, labels, color = '#6366f1' }) {
    const max = Math.max(...data, 1);
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
        {data.map((value, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: `${(value / max) * 100}px`,
              background: `linear-gradient(180deg, ${color}, ${color}88)`,
              borderRadius: '4px 4px 0 0',
              minHeight: 4,
            }} />
            <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7 }}>{labels[i]}</div>
            <div style={{ fontSize: 10, fontWeight: 'bold' }}>{value}</div>
          </div>
        ))}
      </div>
    );
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  return (
    <div className="page">
      <BackButton />
      <h1>📊 {t('analytics.title')}</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { id: 'week', label: t('common.week') },
          { id: 'month', label: t('common.month') },
          { id: 'year', label: t('common.year') },
        ].map(p => (
          <button
            key={p.id}
            className={`btn btn-small ${period === p.id ? 'btn-primary' : ''}`}
            style={{ flex: 1, background: period === p.id ? undefined : 'rgba(255,255,255,0.1)' }}
            onClick={() => setPeriod(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
        <div style={{ background: 'rgba(16,185,129,0.1)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{t('analytics.income')}</div>
          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#10b981' }}>{data?.totals?.income?.toLocaleString() || 0}</div>
        </div>
        <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{t('analytics.expense')}</div>
          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#ef4444' }}>{data?.totals?.expenses?.toLocaleString() || 0}</div>
        </div>
        <div style={{ background: 'rgba(99,102,241,0.1)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{t('analytics.profit')}</div>
          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#6366f1' }}>{data?.totals?.profit?.toLocaleString() || 0}</div>
        </div>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>📈 {t('analytics.income_chart')}</div>
        <BarChart data={data?.income || []} labels={data?.labels || []} color="#10b981" />
      </div>

      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>📉 {t('analytics.expense_chart')}</div>
        <BarChart data={data?.expenses || []} labels={data?.labels || []} color="#ef4444" />
      </div>

      <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>🔮 {t('analytics.tax_forecast')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 8 }}>
            <div style={{ fontSize: 11, opacity: 0.8 }}>VAT</div>
            <div style={{ fontWeight: 'bold' }}>{data?.taxForecast?.vat || 0} PLN</div>
          </div>
          <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 8 }}>
            <div style={{ fontSize: 11, opacity: 0.8 }}>PIT</div>
            <div style={{ fontWeight: 'bold' }}>{data?.taxForecast?.pit || 0} PLN</div>
          </div>
          <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 8 }}>
            <div style={{ fontSize: 11, opacity: 0.8 }}>ZUS</div>
            <div style={{ fontWeight: 'bold' }}>{data?.taxForecast?.zus || 0} PLN</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: 8 }}>
            <div style={{ fontSize: 11, opacity: 0.8 }}>{t('common.total')}</div>
            <div style={{ fontWeight: 'bold', fontSize: 16 }}>{data?.taxForecast?.total || 0} PLN</div>
          </div>
        </div>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>🏆 {t('analytics.top_clients')}</div>
        {(data?.topClients || []).map((client, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', padding: '8px 0',
            borderBottom: i < (data?.topClients?.length || 0) - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
          }}>
            <span>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {client.name}</span>
            <span style={{ fontWeight: 'bold' }}>{client.amount.toLocaleString()} PLN</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 'bold' }}>{data?.totals?.invoices || 0}</div>
          <div style={{ opacity: 0.7 }}>{t('analytics.invoices_count')}</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 'bold' }}>{data?.totals?.clients || 0}</div>
          <div style={{ opacity: 0.7 }}>{t('analytics.clients_count')}</div>
        </div>
      </div>
    </div>
  );
}
